import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  MessageSquareText,
  PanelLeft,
  Settings,
  ShieldCheck,
  Terminal,
  Users,
} from "lucide-react";

import * as constants from '../../constants';
import '../../styles/SideMenu.css';
import '@/styles/design-system.css';

const DASHBOARD_ITEM = {
  id: 'dashboard',
  text: 'Dashboard',
  icon: LayoutDashboard,
  path: constants.ROUTE_BACK_OFFICE_HOME,
  relatedPaths: [constants.ROUTE_BACK_OFFICE_HOME],
};

const SETTINGS_ITEM = {
  id: 'system-settings',
  text: 'System Settings',
  icon: Settings,
  path: constants.ROUTE_BACK_OFFICE_SYSTEM_SETTINGS,
  relatedPaths: [constants.ROUTE_BACK_OFFICE_SYSTEM_SETTINGS],
};

const MENU_SECTIONS = [
  {
    id: 'manage',
    label: 'Manage',
    items: [
      {
        id: 'firms',
        text: 'Firms',
        icon: Building2,
        path: constants.ROUTE_FIRMS,
        relatedPaths: [constants.ROUTE_FIRMS, constants.ROUTE_FIRM_EDIT],
      },
      {
        id: 'usage',
        text: 'Usage Tracking',
        icon: BarChart3,
        path: constants.ROUTE_BILLING_METRICS,
        relatedPaths: [constants.ROUTE_BILLING_METRICS],
      },
      {
        id: 'content-types',
        text: 'Content Types',
        icon: FileText,
        path: constants.ROUTE_BACK_OFFICE_CONTENT_TYPES,
        relatedPaths: [constants.ROUTE_BACK_OFFICE_CONTENT_TYPES],
      },
      {
        id: 'team',
        text: 'Team',
        icon: Users,
        path: constants.ROUTE_BACK_OFFICE_USERS,
        relatedPaths: [constants.ROUTE_BACK_OFFICE_USERS, constants.ROUTE_BACK_OFFICE_USER_EDIT],
      },
    ],
  },
  {
    id: 'ai-prompts',
    label: 'AI & Prompts',
    items: [
      {
        id: 'content-prompts',
        text: 'Content Prompts',
        icon: MessageSquareText,
        path: constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATES,
        relatedPaths: [
          constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATES,
          constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT,
          constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS,
          constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK_EDIT,
          constants.ROUTE_BACK_OFFICE_RESOLVED_PROMPTS,
        ],
      },
      {
        id: 'system-prompts',
        text: 'System Prompts',
        icon: Terminal,
        path: constants.ROUTE_BACK_OFFICE_SYSTEM_PROMPTS,
        relatedPaths: [constants.ROUTE_BACK_OFFICE_SYSTEM_PROMPTS],
      },
      {
        id: 'content-validators',
        text: 'Content Validators',
        icon: ShieldCheck,
        path: constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATORS,
        relatedPaths: [
          constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATORS,
          constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATOR_EDIT,
        ],
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      {
        id: 'support-requests',
        text: 'Support Requests',
        icon: LifeBuoy,
        path: constants.ROUTE_BACK_OFFICE_SUPPORT_REQUESTS,
        relatedPaths: [
          constants.ROUTE_BACK_OFFICE_SUPPORT_REQUESTS,
          constants.ROUTE_BACK_OFFICE_SUPPORT_REQUEST,
        ],
      },
    ],
  },
];

const itemClasses = (selected) =>
  `group relative flex items-center h-[34px] text-nav-item transition-colors ${
    selected
      ? 'bg-nav-activeBg text-nav-text-selected font-medium'
      : 'text-nav-text hover:bg-nav-activeBg'
  }`;

const MenuBar = () => {
  const isMenuOpen = useSelector((state) => state.backOfficeMenuBar.isMenuOpen);
  const location = useLocation();
  const currentPath = location.pathname;

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem('backOfficeSideMenuCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [sectionOpen, setSectionOpen] = useState(() => {
    const savedState = localStorage.getItem('backOfficeSideMenuSections');
    const saved = savedState ? JSON.parse(savedState) : {};
    return MENU_SECTIONS.reduce((acc, section) => {
      acc[section.id] = saved[section.id] ?? true;
      return acc;
    }, {});
  });

  useEffect(() => {
    localStorage.setItem('backOfficeSideMenuCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('backOfficeSideMenuSections', JSON.stringify(sectionOpen));
  }, [sectionOpen]);

  const isSelected = (paths) => {
    if (!paths?.length) return false;
    for (const path of paths) {
      if (currentPath === path) return true;
      if (path !== constants.ROUTE_BACK_OFFICE_HOME && currentPath.startsWith(path)) return true;
    }
    return false;
  };

  const finalIsCollapsed = isMenuOpen ? false : isCollapsed;

  const renderItem = (item) => {
    const selected = isSelected(item.relatedPaths);
    return (
      <Link
        key={item.id}
        to={item.path}
        className={itemClasses(selected)}
        title={finalIsCollapsed ? item.text : undefined}
      >
        {!finalIsCollapsed && selected && (
          <span className="absolute left-0 inset-y-0 my-auto h-nav-active-bar w-0.5 rounded-r-full bg-nav-activeIndicator" />
        )}
        <div className={`flex items-center gap-3 ${finalIsCollapsed ? 'w-full justify-center px-0' : 'px-4'}`}>
          <item.icon className="h-nav-icon w-nav-icon flex-shrink-0 text-nav-icon group-hover:text-current" />
          {!finalIsCollapsed && <span>{item.text}</span>}
        </div>
      </Link>
    );
  };

  return (
    <aside className={`main-menu th${finalIsCollapsed ? ' collapsed' : ''}${isMenuOpen ? ' open' : ''}`}>
      <div className={`flex h-10 shrink-0 items-center ${finalIsCollapsed ? 'justify-center px-0' : 'justify-end px-3'}`}>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex h-7 w-7 items-center justify-center bg-transparent text-nav-heading transition-colors hover:bg-transparent hover:text-nav-iconHover"
          title={finalIsCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <PanelLeft className="size-4" />
        </button>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle pb-4">
        {renderItem(DASHBOARD_ITEM)}

        {MENU_SECTIONS.map((section) => {
          const isOpen = finalIsCollapsed ? true : !!sectionOpen[section.id];
          return (
            <div key={section.id} className="mt-3">
              {!finalIsCollapsed && (
                <button
                  onClick={() =>
                    setSectionOpen((prev) => ({ ...prev, [section.id]: !prev[section.id] }))
                  }
                  className="group flex w-full cursor-pointer items-center justify-between bg-transparent px-4 pb-2 hover:bg-transparent"
                  aria-expanded={isOpen}
                >
                  <span className="text-backoffice-sidebar-heading uppercase font-semibold text-nav-heading">
                    {section.label}
                  </span>
                  {isOpen ? (
                    <ChevronDown className="h-3 w-3 text-nav-heading opacity-0 transition-opacity group-hover:opacity-100" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-nav-heading opacity-0 transition-opacity group-hover:opacity-100" />
                  )}
                </button>
              )}
              <div
                className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
              >
                <div className="min-h-0">
                  <div
                    className={`space-y-0.5 transition-all duration-300 ease-in-out ${
                      isOpen ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
                    }`}
                  >
                    {section.items.map(renderItem)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="shrink-0 pb-2.5">
        <div className="h-px bg-nav-footer-border" />
        <div className="h-1.5" />
        {renderItem(SETTINGS_ITEM)}
      </div>
    </aside>
  );
};

export default MenuBar;